import * as React from 'react';
import { ChevronDown, ChevronRight, Loader2, Save, UserPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input, Textarea } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../components/ui/toast';
import { useCreateTicket } from '../../hooks/useTickets';
import type { TicketCategory, TicketPriority } from '@cp3/shared';

export function CreateTicketDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateTicket();
  const { toast } = useToast();
  const [subject, setSubject] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [category, setCategory] = React.useState<TicketCategory>('general');
  const [priority, setPriority] = React.useState<TicketPriority>('normal');
  const [callerName, setCallerName] = React.useState('');
  const [callerPhone, setCallerPhone] = React.useState('');
  const [callerEmail, setCallerEmail] = React.useState('');
  const [deferUntil, setDeferUntil] = React.useState('');
  const [tags, setTags] = React.useState('');
  // Kontaktfelder sind standardmäßig zugeklappt — sie sind nice-to-have
  // und überfrachten sonst den Erst-Eindruck. Wenn der Kunde im Anruf
  // bereits Daten gesammelt hat, klappt er sie über den Toggle auf.
  const [contactOpen, setContactOpen] = React.useState(false);

  async function onSave() {
    if (!subject.trim() || !description.trim()) {
      toast({
        title: 'Bitte fülle „Worum geht es?" und „Erzähl uns mehr" aus.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const r = await create.mutateAsync({
        subject: subject.trim(),
        description: description.trim(),
        category,
        priority,
        callerName: callerName.trim() || undefined,
        callerPhone: callerPhone.trim() || undefined,
        callerEmail: callerEmail.trim() || undefined,
        deferUntilMs: deferUntil ? new Date(deferUntil).getTime() : undefined,
        tags: tags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, 20),
      });
      toast({ title: `Anfrage ${r.publicCode} gespeichert` });
      onClose();
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Neue Anfrage erstellen</DialogTitle>
          <DialogDescription>
            Lege deine Anfrage hier selbst an. Während eines Anrufs erledigt das
            deine Sprach-KI ganz von alleine für dich.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="subject">Worum geht es?</Label>
            <Input
              id="subject"
              placeholder="Eine kurze und klare Zusammenfassung"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={160}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Erzähl uns mehr</Label>
            <Textarea
              id="description"
              placeholder="Was braucht dein Kunde und was hat er dir mitgeteilt?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              maxLength={8000}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Wie dringend ist es?</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TicketPriority)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">Dringend</SelectItem>
                  <SelectItem value="high">Hoch</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Niedrig</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Worum dreht es sich?</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as TicketCategory)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="billing">Abrechnung</SelectItem>
                  <SelectItem value="technical">Technisch</SelectItem>
                  <SelectItem value="complaint">Beschwerde</SelectItem>
                  <SelectItem value="sales">Vertrieb</SelectItem>
                  <SelectItem value="feedback">Feedback</SelectItem>
                  <SelectItem value="general">Allgemein</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Kontakt-Sektion ist standardmäßig zugeklappt — Pflicht-Felder
              stehen oben, der Kontakt ist nur für die Fälle wo der Anrufer
              das schon mitgeteilt hat. */}
          <div className="rounded-md border border-border">
            <button
              type="button"
              onClick={() => setContactOpen((o) => !o)}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium hover:bg-muted/30"
              aria-expanded={contactOpen}
            >
              <span className="flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-muted-foreground" />
                Kontakt hinzufügen
                {(callerName || callerPhone || callerEmail) && !contactOpen && (
                  <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                    ausgefüllt
                  </span>
                )}
              </span>
              {contactOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            {contactOpen && (
              <div className="border-t border-border p-3">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="callerName">Name des Kunden</Label>
                    <Input
                      id="callerName"
                      value={callerName}
                      onChange={(e) => setCallerName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="callerPhone">Telefonnummer</Label>
                    <Input
                      id="callerPhone"
                      value={callerPhone}
                      onChange={(e) => setCallerPhone(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="callerEmail">E-Mail</Label>
                    <Input
                      id="callerEmail"
                      type="email"
                      value={callerEmail}
                      onChange={(e) => setCallerEmail(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="deferUntil">Auf später verschieben</Label>
              <Input
                id="deferUntil"
                type="date"
                value={deferUntil}
                onChange={(e) => setDeferUntil(e.target.value)}
                placeholder="Wähle ein Datum"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tags">Schlagwörter zum Wiederfinden</Label>
              <Input
                id="tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="Zum Beispiel Stammkunde oder Rückruf"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Verwerfen</Button>
          <Button onClick={onSave} disabled={create.isPending}>
            {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Anfrage speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
